import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from 'react-hook-form';
import FormControl from "@mui/material/FormControl/FormControl";
import FormLabel from "@mui/material/FormLabel/FormLabel";
import RadioGroup from "@mui/material/RadioGroup/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel/FormControlLabel";
import Radio from "@mui/material/Radio/Radio";
import CustomSelect, { IOptions } from "../../components/FormFields/CustomSelect";
import { fetchRoles } from "../../store/rolesThunk";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, SelectChangeEvent, Typography } from '@mui/material';
import LoadingButton from "@mui/lab/LoadingButton/LoadingButton";
import { ApiStatus } from "../../utils/api.types";
import { updateUserInvite, fetchUsers } from "../../store/userThunk";

interface userEdit {
    status: string;
    role: string;
};

interface DialogAddressFormProps {
    title?: string;
    userRow?: any;
    open: boolean;
    onClose: (k: any) => void;
};

const UserEditDrawer: FC<DialogAddressFormProps> = ({ onClose, userRow, open }) => {

    const [userRoles, setUserRoles] = useState<IOptions[]>([]);
    const { status } = useAppSelector((state) => state.users);
    const dispatch = useAppDispatch();
    const [userInviteStatus, setUserInviteStatus] = useState('');
    const [userRoleValues, setUserRoleValues] = useState(userRow?.role?.role);

    useEffect(() => {
        const getRoles = async () => {
            const data: any = await dispatch(fetchRoles());
            const roles = Array.isArray(data?.payload) ? data.payload : [];
            setUserRoles(roles.map((c: any) => {
                return { label: c.role.charAt(0).toUpperCase() + c.role.slice(1), value: c._id }
            }));
        };
        getRoles();
    }, [dispatch]);

    useEffect(() => {
        setUserInviteStatus(userRow?.isActive === true ? 'Active' : 'Inactive');
        setUserRoleValues(userRow?.role?._id);
    }, [open, userRow]);

    const methods = useForm<userEdit>({
        mode: "all",
        shouldUnregister: true
    });
    const { register } = methods;

    const closeDialog = () => {
        onClose('');
    };

    const handleRadioButtons = (e: any) => {
        setUserInviteStatus(e.target.value);
    };

    const onSubmit = async (data: any) => {
        try {
            let userStatus = userInviteStatus === 'Active' ? true : false;
            await dispatch(updateUserInvite({ userId: userRow._id, accountId: userRow.accountId, userUpdatePayload: { isUserVerified: userStatus, role: userRoleValues } })).unwrap();
            closeDialog();
            dispatch(fetchUsers(userRow.accountId));
        } catch (error: any) {
            // Empty catch block.
        }
    };

    return (
        <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
            <FormProvider {...methods}>
                <DialogTitle>
                    <Box sx={{ display: "flex", mt: 1, justifyContent: "space-between" }}>
                        <Typography variant="h5">Edit Member</Typography>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <Grid container columnSpacing={1} rowSpacing={3}>
                        <Grid item xs={12}>
                            <FormControl>
                                <FormLabel id="status-label">Status</FormLabel>
                                <RadioGroup
                                    {...register("status")}
                                    row
                                    onChange={handleRadioButtons}
                                    value={userInviteStatus}
                                    name="status"
                                >
                                    <FormControlLabel
                                        value="Active"
                                        control={<Radio checked={userInviteStatus === 'Active'} />}
                                        label="Active"
                                    />
                                    <FormControlLabel
                                        value="Inactive"
                                        control={<Radio checked={userInviteStatus === 'Inactive'} />}
                                        label="Inactive"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <CustomSelect
                                name="role"
                                label="Role"
                                id="user-role"
                                variant="standard"
                                options={userRoles}
                                value={userRoleValues ?? ""}
                                onChange={(event: SelectChangeEvent<any>) => {
                                    setUserRoleValues(event.target.value);
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 1,
                            pb: 1
                        }}
                    >
                        <Button
                            variant='outlined'
                            onClick={() => {
                                methods.reset();
                                onClose(false);
                            }}
                            sx={{ minWidth: 110, borderRadius: 2 }}
                        >
                            Cancel
                        </Button>
                        <LoadingButton
                            type="submit"
                            onClick={methods.handleSubmit(onSubmit)}
                            loading={status === ApiStatus.LOADING}
                            variant="contained"
                            color="secondary"
                            sx={{ minWidth: 110, borderRadius: 2 }}
                        >
                            Save
                        </LoadingButton>
                    </Box>
                </DialogActions>
            </FormProvider>
        </Dialog>
    );
};

export default UserEditDrawer;
